/**
 * Error Reporting Utilities
 *
 * This file provides utility functions for logging errors, so the console line
 * and the context that explains it stay together at every call site.
 *
 * Errors logged here carry a dedupe suffix that keeps them out of Datadog RUM:
 * RUM reports console errors on its own, and `ignoredErrorMessages` — which its
 * discard filter uses — deliberately drops these. See
 * frontends/app/src/util/datadog/datadogRumDiscardEvent.ts.
 *
 */

export const logDedupeSuffix = '[log:dedupe]';

/**
 * Context logged alongside an error.
 *
 * @property tags - An optional object containing custom tags for the error.
 * @property fingerprint - An optional array of strings identifying related errors.
 */
type ErrorOptions = {
  tags?: Record<string, string>;
  fingerprint?: string[];
};

/**
 * Logs an error to the console with a dedupe suffix.
 *
 * @param error - The error to be logged. Can be a string or an Error object.
 * @param options - Optional. Context logged alongside the error.
 */
export function captureAndLogError(
  error: string | Error,
  options: ErrorOptions = {}
): void {
  const errorMessage = error instanceof Error ? error.message : error;

  console.error(`${errorMessage} ${logDedupeSuffix}`, options);
}

/**
 * Logs an error to the console and throws it with a dedupe suffix.
 *
 * @param error - The error to be logged and thrown. Can be a string or an Error object.
 * @param options - Optional. Context logged alongside the error.
 * @throws {Error} The original error with a dedupe suffix appended to its message.
 */
export function captureAndThrowError(
  error: string | Error,
  options: ErrorOptions = {}
): never {
  const errorMessage = error instanceof Error ? error.message : error;
  const dedupeMessage = `${errorMessage} ${logDedupeSuffix}`;

  console.error(dedupeMessage, options);

  if (error instanceof Error) {
    error.message = dedupeMessage;
    throw error;
  } else {
    throw new Error(dedupeMessage);
  }
}
